import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Ofwca} from '../../../shared/interface/ofwca';
import {Contract} from '../../../shared/interface/contract';
import {Observable, ReplaySubject, zip} from 'rxjs';
import {PartnerService} from '../../../shared/services/partner.service';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {takeUntil} from 'rxjs/operators';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ContractService} from '../../../shared/services/contract.service';
import {ContractOfwcaReassign} from '../../../shared/interface/contract-ofwca-reassign';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {CONTRACT_STATUS_TERMINATED} from '../../../shared/services/dict.service';

export interface OfwcaReassignForm {
  ofwca: Ofwca,
  form: FormGroup,
  ofwcaReassign: ContractOfwcaReassign,
}

@Component({
  selector: 'app-ofwca-to-reassign-list',
  templateUrl: './ofwca-to-reassign-list.component.html',
  styleUrls: ['./ofwca-to-reassign-list.component.scss']
})
export class OfwcaToReassignListComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() contract: Observable<Contract>;
  @Input() disableModificationSwitch = false;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  modify = false;
  contractObject: Contract;
  dataSource: MatTableDataSource<OfwcaReassignForm>;
  displayedColumns: string[] = ['ofwca', 'status', 'currentFacility', 'partner', 'contract', 'facility', ];
  userPermissions: ReplaySubject<UserPermissions>;
  disableModification = false;

  constructor(
    private partnerService: PartnerService,
    private contractService: ContractService,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private auth: AuthService,
  ) {
    this.dataSource = new MatTableDataSource<OfwcaReassignForm>([]);
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.contract
      .pipe(takeUntil(this.destroyed$))
      .subscribe((contract: Contract) => {
        this.contractObject = contract;

        this.loadOfwcas();
      });

    if (this.disableModificationSwitch) {
      this.modify = true;
    }
  }

  loadOfwcas(): void {
    if (this.contractObject.status.key !== CONTRACT_STATUS_TERMINATED) {
      if (this.contractObject.partner) {
        zip(
          this.partnerService.getPartnerOfwcas(this.contractObject.partner.uuid),
          this.contractService.getOfwcas(this.contractObject.uuid)
        ).subscribe((responses: ListResponse<Ofwca>[]) => {
          this.loadReassigns([...responses[0].items, ...responses[1].items])
        });
      } else {
        this.contractService.getOfwcas(this.contractObject.uuid)
          .subscribe((response: ListResponse<Ofwca>) => {
            this.loadReassigns(response.items);
          });
      }
    } else {
      this.disableModification = true;
      this.contractService.getOfwcaReassigns(this.contractObject.uuid)
        .subscribe((ofwcaReassigns: ContractOfwcaReassign[]) => {
          const ofwcaReassignForms: OfwcaReassignForm[] = [];
          ofwcaReassigns.forEach((currentOfwcaReassign: ContractOfwcaReassign) => {
            ofwcaReassignForms.push({
              ofwca: currentOfwcaReassign.ofwca,
              form: this.fb.group({
                ofwca: this.fb.control(currentOfwcaReassign.ofwca.uuid),
                newPartner: this.fb.control(currentOfwcaReassign?.newPartner ? currentOfwcaReassign.newPartner.uuid : ''),
                newPartnerFacility: this.fb.control(currentOfwcaReassign?.newPartnerFacility ? currentOfwcaReassign.newPartnerFacility.id : ''),
                newContract: this.fb.control(currentOfwcaReassign?.newContract ? currentOfwcaReassign.newContract.uuid : ''),
              }),
              ofwcaReassign: currentOfwcaReassign,
            });
          });
          this.dataSource.data = ofwcaReassignForms;

          this.subscribeForms(ofwcaReassignForms);
        });
    }
  }

  loadReassigns(ofwcas: Ofwca[]): void {
    this.contractService.getOfwcaReassigns(this.contractObject.uuid)
      .subscribe((ofwcaReassigns: ContractOfwcaReassign[]) => {
        const ofwcaReassignForms: OfwcaReassignForm[] = [];
        const checkedOfwcas = [];
        ofwcas.forEach((ofwca: Ofwca) => {
          if (checkedOfwcas.includes(ofwca.uuid)) {
            return;
          }

          const currentOfwcaReassign: ContractOfwcaReassign = this.getCurrentOfwcaReassign(ofwcaReassigns, ofwca);

          ofwcaReassignForms.push({
            ofwca,
            form: this.fb.group({
              ofwca: this.fb.control(ofwca.uuid),
              newPartner: this.fb.control(currentOfwcaReassign?.newPartner ? currentOfwcaReassign.newPartner.uuid : ''),
              newPartnerFacility: this.fb.control(currentOfwcaReassign?.newPartnerFacility ? currentOfwcaReassign.newPartnerFacility.id : ''),
              newContract: this.fb.control(currentOfwcaReassign?.newContract ? currentOfwcaReassign.newContract.uuid : ''),
            }),
            ofwcaReassign: currentOfwcaReassign,
          });

          checkedOfwcas.push(ofwca.uuid);
        });
        this.dataSource.data = ofwcaReassignForms;

        this.subscribeForms(ofwcaReassignForms);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  endModification(): void {
    this.modify = false;
    this.loadOfwcas();
  }

  private getCurrentOfwcaReassign(ofwcaReassigns: ContractOfwcaReassign[], ofwca: Ofwca): ContractOfwcaReassign {
    let result = null;
    ofwcaReassigns.forEach((ofwcaReassign: ContractOfwcaReassign) => {
      if (ofwcaReassign.ofwca.uuid === ofwca.uuid) {
        result = ofwcaReassign;
      }
    });

    return result;
  }

  private subscribeForms(ofwcaReassignForms: OfwcaReassignForm[]): void {
    ofwcaReassignForms.forEach((ofwcaReassignForm: OfwcaReassignForm) => {
      ofwcaReassignForm.form.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((formData: any) => {
          this.contractService.updateOfwcaReassign(this.contractObject.uuid, formData)
            .subscribe(() => {
              this.snack.open('Zaktualizowano dane', '', {duration: 2000});
            });
        });
    });
  }

}
